import { CircularProgress, Theme, Typography } from '@mui/material';
import styled from 'styled-components';
import {
  Avatar,
  Flex,
  FlexColumn,
  OverflowTooltip,
  TextField,
} from '@elementary/common-ui/components';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import { severityIconMap, smallStatusIconMap } from 'incidents/utils';
import type { Severity, Status } from 'incidents/types';
import {
  fontSize,
  fontWeight,
  lineHeight,
  space,
} from '@elementary/common-ui/style/utils';
import IncidentTicketList from '../components/IncidentTicketList';
import useIncidentTickets from '../hooks/useIncidentTickets';
import type { IncidentMergeCard } from '../types';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from '../constants';

const SectionLabel = styled(Typography)`
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('semibold')};
  letter-spacing: 0.05em;
  line-height: ${lineHeight('relaxed')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  text-transform: uppercase;
`;

const InputLabel = styled(Typography)`
  font-size: ${fontSize('base')};
  line-height: ${lineHeight('md')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const MetadataLabel = styled(Typography)`
  flex: 0 0 72px;
  font-size: ${fontSize('md')};
  line-height: ${lineHeight('relaxed')};
  color: ${({ theme }) => theme.palette.content.muted};
`;

const FieldValue = styled(Typography)`
  min-width: 0;
  font-size: ${fontSize('md')};
  font-weight: ${fontWeight('medium')};
  line-height: ${lineHeight('relaxed')};
  color: ${({ theme }) => theme.palette.content.primary};
`;

const fieldValueSx = {
  fontSize: fontSize('md'),
};

const fieldValueTypographySx = {
  fontWeight: (theme: Theme) => `${theme.tokens.fontWeight.medium} !important`,
  lineHeight: lineHeight('relaxed'),
};

const ReadRow = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  width: 100%;
`;

const TicketsReadRow = styled(ReadRow)`
  align-items: flex-start;
`;

const ValueGroup = styled(Flex)`
  align-items: center;
  gap: ${space('xs')};
  min-width: 0;
`;

const PanelRoot = styled(FlexColumn)`
  flex: 1;
  max-width: 420px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const TopSection = styled(FlexColumn)`
  gap: ${space('mdPlus')};
  padding: ${space('xl', 'xxl')};
`;

const InputGroup = styled(FlexColumn)`
  gap: ${space('sm')};
`;

const MetadataSection = styled(FlexColumn)`
  gap: ${space('md')};
  padding: ${space('xs', 'xxl', 'xl')};
`;

type Props = {
  title: string;
  userSummary: string;
  onTitleChange: (v: string) => void;
  onUserSummaryChange: (v: string) => void;
  primaryIncident: IncidentMergeCard;
  usersData?: { id: string; name: string; email: string }[];
  disabled?: boolean;
};

const assetLine = (incident: IncidentMergeCard) => {
  const first = incident.monitors[0];
  if (first?.asset?.fqn) return first.asset.fqn;
  if (first?.test?.name) return first.test.name;
  return '—';
};

const MergedIncidentPanel = ({
  title,
  userSummary,
  onTitleChange,
  onUserSummaryChange,
  primaryIncident,
  usersData,
  disabled,
}: Props) => {
  const { assignee, severity, status, events } = primaryIncident;
  const displaySev =
    SEVERITY_DISPLAY[severity.toLowerCase() as keyof typeof SEVERITY_DISPLAY] ??
    SEVERITY_DISPLAY[severity as keyof typeof SEVERITY_DISPLAY] ??
    severity;
  const displayStatus =
    STATUS_DISPLAY[status.toLowerCase() as keyof typeof STATUS_DISPLAY] ??
    status;
  const userData = usersData?.find((u) => u.email === assignee);
  const eventCount = events.length;
  const hasAssignee = !!assignee && assignee !== NO_ASSIGNEE_VALUE;
  const assigneeLabel = hasAssignee ? userData?.name ?? assignee : 'Unassigned';

  const { data: tickets = [], isLoading: isLoadingTickets } =
    useIncidentTickets({ incidentId: primaryIncident.id });

  return (
    <PanelRoot>
      <TopSection>
        <SectionLabel>MERGED INCIDENT</SectionLabel>

        <InputGroup>
          <InputLabel>Title</InputLabel>
          <TextField
            size="large"
            fullWidth
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            disabled={disabled}
          />
        </InputGroup>

        <InputGroup>
          <InputLabel>Description</InputLabel>
          <TextField
            size="large"
            fullWidth
            multiline
            minRows={4}
            placeholder="Add a description for the merged incident..."
            value={userSummary}
            onChange={(e) => onUserSummaryChange(e.target.value)}
            disabled={disabled}
          />
        </InputGroup>
      </TopSection>

      <MetadataSection>
        <ReadRow>
          <MetadataLabel>Severity</MetadataLabel>
          <ValueGroup>
            {severityIconMap[displaySev as Severity]}
            <FieldValue>{displaySev}</FieldValue>
          </ValueGroup>
        </ReadRow>

        <ReadRow>
          <MetadataLabel>Asset</MetadataLabel>
          <OverflowTooltip
            sx={fieldValueSx}
            typographySx={fieldValueTypographySx}
          >
            {assetLine(primaryIncident)}
          </OverflowTooltip>
        </ReadRow>

        <ReadRow>
          <MetadataLabel>Status</MetadataLabel>
          <ValueGroup>
            {smallStatusIconMap[displayStatus as Status]}
            <FieldValue>{displayStatus}</FieldValue>
          </ValueGroup>
        </ReadRow>

        <ReadRow>
          <MetadataLabel>Events</MetadataLabel>
          <FieldValue>{eventCount}</FieldValue>
        </ReadRow>

        <ReadRow>
          <MetadataLabel>Assignee</MetadataLabel>
          <ValueGroup>
            {hasAssignee && (
              <Avatar
                value={{
                  id: userData?.id || assignee,
                  name: userData?.name || assignee,
                }}
                size="small"
              />
            )}
            <FieldValue noWrap title={assigneeLabel}>
              {assigneeLabel}
            </FieldValue>
          </ValueGroup>
        </ReadRow>

        <TicketsReadRow>
          <MetadataLabel>Tickets</MetadataLabel>
          {isLoadingTickets ? (
            <CircularProgress size={14} />
          ) : tickets.length === 0 ? (
            <FieldValue>—</FieldValue>
          ) : (
            <Flex sx={{ flex: 1, minWidth: 0 }} gap={1} flexWrap="wrap">
              <IncidentTicketList tickets={tickets} readOnly />
            </Flex>
          )}
        </TicketsReadRow>
      </MetadataSection>
    </PanelRoot>
  );
};

export default MergedIncidentPanel;
